/**
 * Delete Chat Confirmation Dialog
 *
 * Modal dialog to confirm chat deletion with clear warning message.
 * Follows the design system with proper animations and accessibility.
 */

import { AlertTriangle, Trash2, X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { cn } from '../utils/cn';
import { Button } from './button';
import { Dialog } from './dialog';

type DeleteChatDialogProps = {
  isOpen: boolean;
  sessionTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
};

export const DeleteChatDialog: React.FC<DeleteChatDialogProps> = ({
  isOpen,
  sessionTitle,
  onConfirm,
  onCancel,
  isDeleting = false,
}) => {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Focus the confirm button when dialog opens
  useEffect(() => {
    if (isOpen) {
      confirmButtonRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      size="md"
      disableClose={isDeleting}
      aria-labelledby="delete-dialog-title"
      className="py-6 px-10"
    >
      {/* Close button */}
      <button
        onClick={onCancel}
        disabled={isDeleting}
        className={cn(
          'absolute top-4 right-4 p-1.5 rounded-md',
          'text-muted-foreground hover:text-foreground',
          'hover:bg-accent transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
        aria-label="Close dialog"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Warning icon */}
      <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-destructive/10">
        <AlertTriangle className="w-6 h-6 text-destructive" />
      </div>

      {/* Title */}
      <h2
        id="delete-dialog-title"
        className="text-lg font-semibold text-center text-foreground mb-2"
      >
        Delete Chat?
      </h2>

      {/* Description */}
      <p className="text-sm text-muted-foreground text-center mb-2">
        Are you sure you want to delete "{sessionTitle}" chat? This action cannot be undone.
      </p>

      {/* Actions */}
      <div className="flex gap-3 w-full justify-between">
        <Button onClick={onCancel} disabled={isDeleting} variant="outline">
          Cancel
        </Button>
        <Button
          ref={confirmButtonRef}
          onClick={onConfirm}
          disabled={isDeleting}
          variant="destructive"
        >
          {isDeleting ? (
            <>
              <div className="w-4 h-4 border-2 border-destructive-foreground/30 border-t-destructive-foreground rounded-full animate-spin" />
              Deleting...
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4" />
              Delete
            </>
          )}
        </Button>
      </div>
    </Dialog>
  );
};

export default DeleteChatDialog;
